import ErrorMessageToast from "src/components/ErrorMessageToast/ErrorMessageToast";
import styles from "src/components/layout/EventCardLarge/eventCardLarge.module.css";
import EventCardMenuModal from "src/features/Event/components/EventCardMenuModal/EventCardMenuModal";
import { BiSolidBadgeCheck } from "react-icons/bi";
import imageLoadErrorHandler from "src/utils/imageLoadErrorHandler";
import BookMarkIcon from "src/assets/icon/bookmark_icon.svg";
import MarqueeLiveTitle from "src/components/elements/MarqueeLiveTitle/MarqueeLiveTitle";
import CommentIcon from "src/assets/icon/comment_icon_outline.svg";
import LikesIcon from "src/assets/icon/like_icon_outline.svg";
import { operatedBookMarksListAtom } from "src/features/Event/event.atom";
import { useAtom } from "jotai";
import type { Bookmark } from "src/features/Event/event.type";
import getCurrentUserId from "src/utils/currentUserId";
import { useEffect, useState } from "react";
import toggleEventBookMarks from "src/utils/toggleEventBookMarks";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import CertificationMark from "src/components/elements/CertificationMark/CertificationMark";
import Dialog from "../Dialog/Dialog";

type EventCardLargeProps = {
    handleCardClicked: () => void;
    handleMenuButton: () => void;
    event: {
        event_id: number;
        title: string;
        place: string;
        thumbnail: string;
        eventType: string;
    };
    eventDayTime: string;
    user: {
        userId: number;
        username: string;
        userIconPath: string;
        certification: string;
    };
    isLiked: boolean;
    isLikesCount: number;
    bookmarks: Bookmark[];
    commentsCount: number;
    isModalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const EventCardLarge: React.FC<EventCardLargeProps> = (props) => {
    const currentUserId = getCurrentUserId();
    const navigate = useNavigate();

    const [operatedBookMarksList, setOperatedBookMarksList] = useAtom(
        operatedBookMarksListAtom
    );

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const [isBookMarked, setIsBookMarked] = useState<boolean>(
        operatedBookMarksList[props.event.event_id] ??
            props.bookmarks.some((bookmark: { user_id: number }) => {
                return bookmark.user_id === currentUserId;
            })
    );

    const handleBookMarks = () => {
        if (!currentUserId) {
            setIsModalOpen(true);
            return;
        }

        try {
            toggleEventBookMarks(
                props.event.event_id,
                setOperatedBookMarksList
            );
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    useEffect(() => {
        // recordの中にevent_idが存在するかどうか
        if (operatedBookMarksList[props.event.event_id] !== undefined) {
            setIsBookMarked(operatedBookMarksList[props.event.event_id]);
        }
    }, [operatedBookMarksList, props.event.event_id]);

    const navigateArtistProfile = (artistId: number) => {
        if (artistId === currentUserId) {
            navigate("/profile");
            return;
        }
        navigate(`/artist/${artistId}`);
    };

    return (
        <>
            <div
                className={styles.card_container}
                onClick={props.handleCardClicked}
                onKeyDown={props.handleCardClicked}
                role="link"
                tabIndex={0}
            >
                <div className={styles.top_container}>
                    <div
                        className={styles.navigation_button_container}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleBookMarks();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            handleBookMarks();
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="ブックマークする"
                    >
                        <div className={isBookMarked ? styles.active : ""}>
                            <img
                                className={styles.navigation_icon}
                                src={BookMarkIcon}
                                alt=""
                            />
                        </div>
                    </div>
                    <div className={styles.img_container}>
                        <img
                            src={props.event.thumbnail}
                            alt=""
                            decoding="async"
                            loading="lazy"
                            onError={(e) => {
                                return imageLoadErrorHandler(e, "thumbnail");
                            }}
                        />
                    </div>
                </div>
                <div className={styles.center_container}>
                    <div className={styles.date_menu_container}>
                        <p className={styles.day_text}>{props.eventDayTime}</p>
                        <div
                            className={styles.menu_button_container}
                            onClick={(e) => {
                                e.stopPropagation();
                                props.handleMenuButton();
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                props.handleMenuButton();
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="イベントメニューを開く"
                        >
                            <div className={styles.menu_button_icon} />
                        </div>
                    </div>
                    <div className={styles.title_container}>
                        <MarqueeLiveTitle
                            liveTitle={props.event.title}
                            limit={15}
                        />
                    </div>
                    <div className={styles.place_container}>
                        <div className={styles.pin_container}>
                            <div className={styles.pin} />
                        </div>
                        <p className={styles.place_text}>{props.event.place}</p>
                    </div>
                </div>
                {props.event.eventType === "street_event" && (
                    <div className={styles.certification_text_container}>
                        <BiSolidBadgeCheck size="1.3rem" color="#59c567" />
                        <p className={styles.certification_text}>
                            まちなかパフォーマンス公認ライブ
                        </p>
                    </div>
                )}
                <div className={styles.bottom_container}>
                    <div className={styles.user_info_container}>
                        <div
                            className={styles.icon_container}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateArtistProfile(props.user.userId);
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                navigateArtistProfile(props.user.userId);
                            }}
                            role="link"
                            tabIndex={0}
                            aria-label="プロフィールへ遷移"
                        >
                            <div className={styles.icon}>
                                <img
                                    src={props.user.userIconPath}
                                    alt=""
                                    decoding="async"
                                    loading="lazy"
                                    onError={(e) => {
                                        return imageLoadErrorHandler(e, "icon");
                                    }}
                                />
                            </div>
                        </div>
                        <div className={styles.name_container}>
                            <p className={styles.name_text}>
                                {props.user.username}
                            </p>
                            {props.user.certification === "kobe_certified" && (
                                <div className={styles.certification_icon}>
                                    <CertificationMark />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.event_info_container}>
                        <div className={styles.comment_container}>
                            <div className={styles.info_icon_container}>
                                <img
                                    src={CommentIcon}
                                    alt=""
                                    decoding="async"
                                    loading="lazy"
                                />
                            </div>
                            <p className={styles.counter_text}>
                                {props.commentsCount}
                            </p>
                        </div>
                        <div className={styles.like_container}>
                            <div className={styles.info_icon_container}>
                                <img
                                    src={LikesIcon}
                                    alt=""
                                    decoding="async"
                                    loading="lazy"
                                />
                            </div>
                            <p className={styles.counter_text}>
                                {props.isLikesCount}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <EventCardMenuModal
                isModalOpen={props.isModalOpen}
                setIsModalOpen={props.setModalOpen}
                event={{
                    userId: props.user.userId,
                    id: props.event.event_id,
                    title: props.event.title,
                    place: props.event.place,
                    name: props.user.username,
                    eventType: props.event.eventType,
                }}
            />
            <Dialog
                isOpen={isModalOpen}
                title="ログインしてください"
                content={
                    <>
                        <p>
                            ブックマーク機能を利用するにはログインが必要です。
                        </p>
                        <br />
                        <p>
                            ブックマークをすると、ブックマークしたイベントを一覧で日付ごとに確認することができます。
                        </p>
                    </>
                }
                onYes={{
                    label: "ログインする",
                    onClick: () => {
                        navigate("/login");
                    },
                }}
                onNo={{
                    label: "閉じる",
                    onClick: () => {
                        return setIsModalOpen(false);
                    },
                }}
                handleClose={() => {
                    return setIsModalOpen(false);
                }}
            />
            <ErrorMessageToast />
        </>
    );
};

export default EventCardLarge;
